import { scoreAuditDate } from "./scoreAuditDate.js";

export const EMPTY_AUDIT_FILTERS = { search: "", participant: "", entry: "", device: "", action: "" };
export const MAX_AUDIT_EVENTS = 50000;
export const MAX_AUDIT_BYTES = 256 * 1024 * 1024;
const MAX_STALLED_PAGES = 8;
const object = value => value !== null && typeof value === "object" && !Array.isArray(value);
const stable = value => Array.isArray(value) ? value.map(stable) : object(value)
  ? Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])])) : value;
export const auditFingerprint = value => JSON.stringify(stable(value));
export const auditLabel = value => String(value || "Unknown").replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/[_-]+/g, " ").replace(/^./, letter => letter.toUpperCase());
export const auditDate = scoreAuditDate;

export function emptyAuditCollection(matchId, discipline) {
  return { matchId, discipline, matchName: null, sources: [], events: [], pages: 0, complete: false,
    nextToken: null, sourceOccurrences: 0, eventOccurrences: 0, collectionStartedAt: null, collectionFinishedAt: null };
}

function fail(message) {
  const error = new Error(message);
  error.status = 409;
  throw error;
}

function validateAuditPage(previous, page) {
  if (!object(page) || page.schemaVersion !== 1 || page.matchId !== previous.matchId || page.discipline !== previous.discipline ||
      !Array.isArray(page.sources) || !Array.isArray(page.events) || typeof page.complete !== "boolean" ||
      (page.complete ? page.nextToken !== null : typeof page.nextToken !== "string" || !page.nextToken) ||
      page.sourceCount !== page.sources.length || page.eventCount !== page.events.length ||
      page.consistency !== "non_transactional_collection" || page.countSemantics !== "page_source_occurrences") {
    fail("The audit response is incomplete or belongs to a different match. Restart collection.");
  }
  if (previous.complete) fail("Audit collection is already complete. Restart collection for a fresh read.");
}

function capacityLimit(kind) {
  const error = new Error(`Browser exports are limited to 50,000 unique events and 256 MiB of retained data. This page would exceed the ${kind === "events" ? "event" : "data-size"} limit. No complete file was created. Changing filters or retrying cannot reduce this collection. A larger export requires a separate export workflow.`);
  error.code = "AUDIT_CAPACITY_LIMIT";
  error.limit = kind;
  throw error;
}

export function createAuditCollector(matchId, discipline, { initial, maxEvents = MAX_AUDIT_EVENTS, maxBytes = MAX_AUDIT_BYTES } = {}) {
  const encoder = new TextEncoder();
  const entry = value => ({ value, bytes: encoder.encode(JSON.stringify(value)).byteLength });
  const sources = new Map((initial?.sources || []).map(value => [value.sourceId, entry(value)]));
  const events = new Map((initial?.events || []).map(value => [value.identity, entry(value)]));
  let retainedBytes = encoder.encode('{"sources":[],"events":[]}').byteLength;
  for (const map of [sources, events]) {
    for (const item of map.values()) retainedBytes += item.bytes;
    retainedBytes += Math.max(0, map.size - 1);
  }
  let metadata = { ...(initial || emptyAuditCollection(matchId, discipline)) };
  delete metadata.sources;
  delete metadata.events;
  let stalledPages = 0;
  const tokens = new Set(initial?.nextToken ? [initial.nextToken] : []);
  const eventLimit = Math.min(MAX_AUDIT_EVENTS, maxEvents), byteLimit = Math.min(MAX_AUDIT_BYTES, maxBytes);
  function append(page) {
    validateAuditPage(metadata, page);
    if (page.nextToken && tokens.has(page.nextToken)) fail("Audit pagination did not advance. Restart collection.");
    const sourceDelta = new Map(), eventDelta = new Map();
    let bytes = retainedBytes, sourceCount = sources.size, eventCount = events.size, progressed = false;
    function stage(map, delta, key, value, kind) {
      const old = delta.get(key) || map.get(key), next = entry(value);
      bytes += next.bytes - (old?.bytes || 0);
      if (!old) {
        const count = kind === "source" ? sourceCount++ : eventCount++;
        if (count) bytes++;
        progressed = true;
      }
      if (eventCount > eventLimit) capacityLimit("events");
      if (bytes > byteLimit) capacityLimit("bytes");
      delta.set(key, next);
    }
    for (const source of page.sources) {
    if (!object(source) || typeof source.sourceId !== "string" || !source.sourceId || typeof source.revision !== "string" ||
        !object(source.context) || typeof source.kind !== "string" || typeof source.status !== "string") {
      fail("An audit source has missing identity or revision information.");
    }
    const old = (sourceDelta.get(source.sourceId) || sources.get(source.sourceId))?.value;
    if (old && (old.revision !== source.revision || old.kind !== source.kind)) {
      fail("An audit source changed during collection. Restart collection.");
    }
    stage(sources, sourceDelta, source.sourceId, source, "source");
  }
  for (const item of page.events) {
    if (!object(item) || !object(item.auditEvent) || typeof item.auditEvent.id !== "string" || !item.auditEvent.id ||
        !["outcome_decision", `${discipline}_score`].some(namespace => item.identity === `${namespace}:${item.auditEvent.id}`) ||
        (!sources.has(item.sourceId) && !sourceDelta.has(item.sourceId)) || !object(item.context) ||
        (item.auditEvent.matchId && item.auditEvent.matchId !== matchId)) {
      fail("An audit event has missing or conflicting identity. No complete file is available.");
    }
    const old = (eventDelta.get(item.identity) || events.get(item.identity))?.value;
    if (old && auditFingerprint(old.auditEvent) !== auditFingerprint(item.auditEvent)) {
      fail(`Conflicting immutable audit event ${item.auditEvent.id}. Restart collection; neither version was accepted as complete.`);
    }
    const reference = { sourceId: item.sourceId, context: item.context, receipt: item.receipt ?? null };
    const refs = old?.sourceReferences || [];
    const knownReference = refs.some(ref => auditFingerprint(ref) === auditFingerprint(reference));
    if (old && knownReference) continue;
    progressed = true;
    stage(events, eventDelta, item.identity, { identity: item.identity, auditEvent: old?.auditEvent || item.auditEvent,
      sourceReferences: [...refs, reference] }, "event");
  }
    const nextStalledPages = progressed ? 0 : stalledPages + 1;
    if (!page.complete && nextStalledPages >= MAX_STALLED_PAGES) fail("Audit pagination returned no new retained data for eight pages. Restart collection.");
    // Commit only after the entire page, cursor and capacity checks succeed.
    sourceDelta.forEach((value, key) => sources.set(key, value));
    eventDelta.forEach((value, key) => events.set(key, value));
    if (page.nextToken) tokens.add(page.nextToken);
    stalledPages = nextStalledPages;
    retainedBytes = bytes;
    metadata = { ...metadata, matchName: page.matchName || metadata.matchName,
      pages: metadata.pages + 1, complete: page.complete, nextToken: page.nextToken,
      sourceOccurrences: metadata.sourceOccurrences + page.sourceCount, eventOccurrences: metadata.eventOccurrences + page.eventCount,
      collectionStartedAt: metadata.collectionStartedAt || page.collectionStartedAt,
      collectionFinishedAt: page.collectionFinishedAt, consistency: page.consistency, historyCoverage: page.historyCoverage, retainedBytes };
    return { ...metadata, uniqueEventCount: events.size, uniqueSourceCount: sources.size };
  }
  return { append, snapshot: () => ({ ...metadata, retainedBytes,
    sources: Array.from(sources.values(), item => item.value), events: Array.from(events.values(), item => item.value) }) };
}

export function appendAuditPage(previous, page) {
  const collector = createAuditCollector(previous.matchId, previous.discipline, { initial: previous });
  collector.append(page);
  return collector.snapshot();
}

export function auditContext(item) {
  const contexts = item.sourceReferences.map(ref => ref.context);
  return Object.fromEntries(["participantId", "entryId", "competitorName", "entryLabel", "stageId", "stageName", "stringId", "stringName"]
    .map(key => [key, contexts.find(context => context[key])?.[key] || item.auditEvent[key] || null]));
}

function contextsFor(item) { return [item.auditEvent, ...item.sourceReferences.map(ref => ref.context)]; }
function actionKey(item) { return item.auditEvent.source === "discarded_local_decision" ? "participant_outcome_discarded" : item.auditEvent.action || "unknown"; }

export function shortAuditIds(values) {
  const ids = [...new Set(values.filter(value => typeof value === "string" && value))];
  const normalized = new Map(ids.map(id => [id, id.replace(/-participant$/, "")]));
  const labels = new Map(ids.map(id => {
    const value = normalized.get(id);
    return [id, value.length <= 12 ? value : value.slice(0, 8)];
  }));
  // Expand only collisions, including IDs whose shared participant suffix was removed.
  for (let length = 12; length <= Math.max(12, ...ids.map(id => id.length)) + 4; length += 4) {
    const counts = new Map();
    labels.forEach(label => counts.set(label, (counts.get(label) || 0) + 1));
    const collisions = ids.filter(id => counts.get(labels.get(id)) > 1);
    if (!collisions.length) break;
    for (const id of collisions) {
      const value = normalized.get(id);
      labels.set(id, ids.some(other => other !== id && normalized.get(other) === value) ? id : value.slice(0, length));
    }
  }
  return labels;
}

export function auditDisplayIds(events) {
  return {
    participant: shortAuditIds(events.flatMap(item => contextsFor(item).map(context => context.participantId))),
    entry: shortAuditIds(events.flatMap(item => contextsFor(item).map(context => context.entryId))),
    device: shortAuditIds(events.map(item => item.auditEvent.deviceId)),
    actor: shortAuditIds(events.map(item => item.auditEvent.actorUserId)),
  };
}

export function auditActorLabel(event, ids = new Map()) {
  const id = event.actorUserId, label = event.actorLabel;
  if (!id) return label ? `${label} (ID not recorded)` : "Unknown historical actor";
  return !label || label.trim().toLowerCase() === id.toLowerCase() ? `Account ${ids.get(id) || id}` : label;
}

export function auditDeviceLabel(event, ids = new Map()) {
  if (!event.deviceId) return event.deviceName ? `${event.deviceName} (ID not recorded)` : "Unknown original device";
  return `${event.deviceName || "Device"} (${ids.get(event.deviceId) || event.deviceId})`;
}

export function filterAuditEvents(events, filters) {
  const query = filters.search.trim().toLowerCase();
  return events.filter(item => {
    const event = item.auditEvent;
    const contexts = contextsFor(item);
    if (filters.participant && !contexts.some(context => context.participantId === filters.participant)) return false;
    if (filters.entry && !contexts.some(context => context.entryId === filters.entry)) return false;
    if (filters.device && (originalTraceUnavailable(item) ? "unknown" : event.deviceId || "unknown") !== filters.device) return false;
    if (filters.action && actionKey(item) !== filters.action) return false;
    return !query || auditFingerprint([event, item.sourceReferences]).toLowerCase().includes(query);
  });
}

export function auditFilterOptions(events, ids = auditDisplayIds(events)) {
  const options = { participant: new Map(), entry: new Map(), device: new Map(), action: new Map() };
  for (const item of events) {
    const event = item.auditEvent;
    for (const context of contextsFor(item)) {
      if (context.participantId) options.participant.set(context.participantId, `${context.competitorName || "Participant"} (${ids.participant.get(context.participantId)})`);
      if (context.entryId) options.entry.set(context.entryId, `${context.entryLabel || "Entry"} (${ids.entry.get(context.entryId)})`);
    }
    const originalDeviceId = originalTraceUnavailable(item) ? null : event.deviceId;
    options.device.set(originalDeviceId || "unknown", originalDeviceId ? auditDeviceLabel(event, ids.device) : "Unknown original device");
    options.action.set(actionKey(item), auditLabel(actionKey(item)));
  }
  return Object.fromEntries(Object.entries(options).map(([key, values]) => [key,
    [...values.entries()].sort((a, b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]))]));
}

export function orderAuditEvents(events, order = "newest") {
  const dates = new Map(events.map(item => [item.identity, auditDate(item.auditEvent.createdAt)?.getTime()]));
  return [...events].sort((a, b) => {
    const left = dates.get(a.identity), right = dates.get(b.identity);
    if (left === undefined) return right === undefined ? a.identity.localeCompare(b.identity) : 1;
    if (right === undefined) return -1;
    return (order === "oldest" ? left - right : right - left) || a.identity.localeCompare(b.identity);
  });
}

function state(event, side) {
  let value = event[side] ?? event[`${side}Json`];
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { return { recordedValue: value }; }
  }
  if (Array.isArray(value) && String(event.action).startsWith("participant_outcome")) {
    return Object.fromEntries((side === "before" ? ["outcomeStatus", "decisionId"] : ["outcomeStatus", "reasonCode", "reasonLabel", "notes"])
      .map((key, index) => [key, value[index]]).filter(([, item]) => item !== undefined));
  }
  return value;
}

function flatten(value, path = "", result = {}) {
  if (/signature.*(base64|json)|signatureData/i.test(path)) {
    result[path] = value ? "Signature evidence recorded" : "No signature evidence";
  } else if (object(value) && Object.keys(value).length) {
    for (const [key, item] of Object.entries(value)) flatten(item, path ? `${path}.${key}` : key, result);
  } else if (Array.isArray(value) && value.some(object)) {
    value.forEach((item, index) => flatten(item, `${path}.${index + 1}`, result));
  } else if (value !== undefined) {
    result[path || "recordedValue"] = value;
  }
  return result;
}

export function auditChanges(event) {
  const beforeState = state(event, "before"), afterState = state(event, "after");
  const before = flatten(beforeState), after = flatten(afterState);
  const containers = { before: new Map(), after: new Map() };
  function findContainers(value, path, target) {
    if (/signature.*(base64|json)|signatureData/i.test(path)) return;
    if (object(value)) {
      if (path) target.set(path, value);
      for (const [key, child] of Object.entries(value)) findContainers(child, path ? `${path}.${key}` : key, target);
    } else if (Array.isArray(value)) {
      if (!value.length || value.some(object)) target.set(path, value);
      value.forEach((child, index) => findContainers(child, `${path}.${index + 1}`, target));
    }
  }
  findContainers(beforeState, "", containers.before); findContainers(afterState, "", containers.after);
  const displays = { before: new Map(), after: new Map() }, missingParents = { before: new Map(), after: new Map() };
  const removedLists = [];
  const description = value => Array.isArray(value) ? value.length ? `${value.length} recorded ${value.length === 1 ? "item" : "items"}` : "Empty list"
    : object(value) ? `Recorded object (${Object.keys(value).length} ${Object.keys(value).length === 1 ? "field" : "fields"})` : displayAuditValue(value);
  for (const key of new Set([...containers.before.keys(), ...containers.after.keys()])) {
    const left = containers.before.has(key) ? containers.before.get(key) : before[key];
    const right = containers.after.has(key) ? containers.after.get(key) : after[key];
    const list = Array.isArray(left) || Array.isArray(right);
    if (list || object(left) !== object(right)) {
      before[key] = left; after[key] = right;
      displays.before.set(key, description(left)); displays.after.set(key, description(right));
    }
    for (const [side, value] of [["before", left], ["after", right]]) {
      if (!object(value) && !Array.isArray(value)) missingParents[side].set(key, value === null ? "Parent is none" : "Parent not recorded");
      else if (Array.isArray(value) && !value.length) missingParents[side].set(key, "Not included in recorded list");
    }
    // An explicitly empty recorded list is not evidence that each prior field was deleted.
    if (Array.isArray(left) && left.some(object) && Array.isArray(right) && !right.length) removedLists.push(`${key}.`);
  }
  function inheritedDisplay(side, key) {
    if (displays[side].has(key)) return displays[side].get(key);
    if ((side === "before" ? before : after)[key] !== undefined) return undefined;
    return [...missingParents[side]].filter(([path]) => key.startsWith(`${path}.`)).sort((a, b) => b[0].length - a[0].length)[0]?.[1];
  }
  function priority(field) {
    const leaf = field.key.split(".").pop();
    if (/^(status|statusRaw|outcomeStatus|reviewState|isComplete)$/.test(leaf)) return 0;
    if (/At$|timestamp|time$/i.test(leaf)) return 1;
    if (/^(points|xCount|shotsFired|shotMarks|shots|rawTime|finalTime|penaltyTime|refirePoints)$/.test(leaf)) return 2;
    if (/^id$|Ids?$|Key$|digest|sha256|version/i.test(leaf)) return 6;
    if (/name|signedBy|present|notes|reason|evidence/i.test(leaf)) return 3;
    if (displays.before.has(field.key) || displays.after.has(field.key)) return 4;
    return 5;
  }
  return [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(key => !removedLists.some(path => key.startsWith(path))).map(key => ({ key,
    label: key.split(".").map(auditLabel).join(" / "), before: before[key], after: after[key],
    beforeDisplay: inheritedDisplay("before", key), afterDisplay: inheritedDisplay("after", key),
    changed: auditFingerprint(before[key]) !== auditFingerprint(after[key]) }))
    .sort((a, b) => priority(a) - priority(b) || a.key.split(".").length - b.key.split(".").length || a.label.localeCompare(b.label));
}

const scoreStatusLabels = new Map([
  ["notStarted", "Not started"], ["inProgress", "In progress"], ["completed", "Completed"],
  ["pendingRefire", "Pending refire"], ["refired", "Refired"], ["finalized", "Finalized"],
  ["confirmed", "Confirmed"], ["draft", "Draft"], ["official", "Official"],
]);

export function displayAuditValue(value, fieldKey = "") {
  if (/(^|\.)(status|statusRaw)$/.test(fieldKey) && scoreStatusLabels.has(value)) return scoreStatusLabels.get(value);
  if (value === undefined) return "Not recorded";
  if (value === null) return "None";
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (Array.isArray(value)) return value.map(item => item === null ? "_" : typeof item === "object" ? JSON.stringify(item) : String(item)).join(", ") || "Empty";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value) || "Empty";
}

export function auditHistoryGaps(sources) {
  return sources.filter(source => !["recorded", "recorded_from_first_upload", "derived_summary"].includes(source.status) || source.originalTraceStatus === "original_actor_trace_unavailable");
}

export function auditDelivery(item) {
  const statuses = item.sourceReferences.map(ref => ref.receipt?.deliveryStatus);
  if (statuses.includes("accepted_decision")) return { label: "Accepted decision", tone: "accepted" };
  if (statuses.includes("discarded_local_decision") || item.auditEvent.source === "discarded_local_decision") {
    return { label: "Discarded locally / Not applied to cloud", tone: "not-applied" };
  }
  if (statuses.includes("historical_local_audit")) return { label: "Historical delivery / No cloud change by this delivery", tone: "not-applied" };
  if (statuses.includes("legacy_snapshot")) return { label: "Legacy snapshot", tone: "unknown" };
  return null;
}

export function originalTraceUnavailable(item) {
  return item.identity.startsWith("outcome_decision:");
}

function auditExportDocument(collection, scope, filters) {
  if (!collection.complete || collection.nextToken || !["full", "filtered"].includes(scope)) {
    throw new Error("Only a successfully completed collection can be downloaded.");
  }
  const events = scope === "filtered" ? filterAuditEvents(collection.events, filters) : collection.events;
  const sourceIds = scope === "filtered" ? new Set(events.flatMap(item => item.sourceReferences.map(ref => ref.sourceId))) : null;
  return { schemaVersion: 1, matchId: collection.matchId, discipline: collection.discipline,
    matchName: collection.matchName, exportedAt: new Date().toISOString(), exportScope: scope,
    completeness: { collectionComplete: true, includesAllRetainedSources: scope === "full",
      consistency: collection.consistency, historyCoverage: collection.historyCoverage,
      collectionStartedAt: collection.collectionStartedAt, collectionFinishedAt: collection.collectionFinishedAt,
      loadedPages: collection.pages, collectedUniqueEvents: collection.events.length, collectedUniqueSources: collection.sources.length,
      eventSourceOccurrences: collection.eventOccurrences, sourceOccurrences: collection.sourceOccurrences },
    filters: scope === "filtered" ? { ...filters } : null, uniqueEventCount: events.length,
    sources: scope === "full" ? collection.sources : collection.sources.filter(source => sourceIds.has(source.sourceId)), events };
}

export function buildCompetitionAuditExport(collection, scope, filters = EMPTY_AUDIT_FILTERS) {
  return JSON.stringify(auditExportDocument(collection, scope, filters), null, 2);
}

export async function buildCompetitionAuditBlob(collection, scope, filters = EMPTY_AUDIT_FILTERS, { signal } = {}) {
  const document = auditExportDocument(collection, scope, filters);
  const parts = [];
  let pending = [], pendingLength = 0, records = 0;
  function checkAbort() { if (signal?.aborted) throw new DOMException("Audit download cancelled", "AbortError"); }
  function flush() {
    if (pendingLength) parts.push(new Blob(pending));
    pending = []; pendingLength = 0;
  }
  function write(text) {
    pending.push(text); pendingLength += text.length;
    if (pendingLength >= 64 * 1024) flush();
  }
  async function yieldIfNeeded() {
    if (++records % 128 === 0) await new Promise(resolve => setTimeout(resolve, 0));
    checkAbort();
  }
  checkAbort();
  write("{");
  let firstField = true;
  for (const [key, value] of Object.entries(document)) {
    if (value === undefined) continue;
    write(`${firstField ? "" : ","}${JSON.stringify(key)}:`);
    firstField = false;
    if (key !== "sources" && key !== "events") { write(JSON.stringify(value)); continue; }
    write("[");
    for (let index = 0; index < value.length; index++) {
      if (index) write(",");
      if (key === "sources") write(JSON.stringify(value[index]));
      else {
        const item = value[index];
        write(`{"identity":${JSON.stringify(item.identity)},"auditEvent":${JSON.stringify(item.auditEvent)},"sourceReferences":[`);
        for (let ref = 0; ref < item.sourceReferences.length; ref++) {
          write(`${ref ? "," : ""}${JSON.stringify(item.sourceReferences[ref])}`);
          await yieldIfNeeded();
        }
        write("]}");
      }
      await yieldIfNeeded();
    }
    write("]");
  }
  write("}"); flush(); checkAbort();
  return new Blob(parts, { type: "application/json" });
}
